import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { type FC, useState } from "react";

import { m } from "@recobra/i18n/messages";
import { OPEN_TASK_STATUSES } from "@recobra/schemas/task";
import { SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem } from "@recobra/ui/sidebar";

import { useAuth } from "../lib";
import { casesApi, tasksApi } from "../lib/api";
import { TaskModal, taskContextIdentifier } from "../tasks";
import { useTaskName } from "../tasks/task-data";

export const CaseTaskSidebarItems: FC = () => {
  const taskName = useTaskName();
  const { caseNumber: caseNumberParam } = useParams({ strict: false });
  const caseNumber = caseNumberParam ? Number(caseNumberParam) : NaN;
  const { user } = useAuth();
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);

  const { data: kase } = useQuery({
    queryKey: ["cases", caseNumber],
    queryFn: () => casesApi.getCase(caseNumber),
    enabled: Number.isInteger(caseNumber),
  });

  const { data: caseTasks } = useQuery({
    queryKey: ["tasks", "case", kase?.id],
    queryFn: () =>
      tasksApi.listTasks({ contextType: "case", contextId: kase?.id ?? "", limit: 100 }),
    enabled: !!kase?.id,
  });

  const myOpenTasks = (caseTasks?.items ?? []).filter(
    (task) => task.assignedTo === user.username && OPEN_TASK_STATUSES.includes(task.status),
  );

  if (myOpenTasks.length === 0) return null;

  const activeTask = myOpenTasks.find((task) => task.id === activeTaskId) ?? null;

  return (
    <>
      <SidebarMenuSub>
        {myOpenTasks.map((task) => (
          <SidebarMenuSubItem key={task.id}>
            <SidebarMenuSubButton
              render={<button type="button" />}
              onClick={() => setActiveTaskId(task.id)}
              className="h-auto flex-col items-start gap-0 py-1.5"
            >
              <span className="truncate">{taskName(task.type)}</span>
              <span className="text-xs text-muted-foreground">
                {m.task_context_type_label({ contextType: task.contextType })}{" "}
                {taskContextIdentifier(task)}
              </span>
            </SidebarMenuSubButton>
          </SidebarMenuSubItem>
        ))}
      </SidebarMenuSub>
      {activeTask ? (
        <TaskModal
          taskId={activeTask.id}
          placeholderTask={activeTask}
          onOpenChange={(open) => !open && setActiveTaskId(null)}
        />
      ) : null}
    </>
  );
};
